import React, {memo, useCallback, useState, useMemo, useEffect} from "react"
import {PasswordContainer} from "../../../../components/PasswordContainer"
import {SettingsPageContainer} from "../../../../components/Settings/SettingsPageContainer"
import {ColumnModel, SettingsList} from "../../../../components/Settings/SettingsList"
import {useLocalization} from "../../../../hooks/localization"
import {useRPC} from "../../../../hooks/rpc"
import {useSelector} from "react-redux"
import {UserModel} from "../../../../types/control"
import {hasAuthority} from "../../../../utils/role"

const keyExtractor = (item: UserModel) => item.id
const classExtractor = (item: UserModel) => item.role
export const UserInfo = memo(function UserInfo() {
    const {t} = useLocalization()
    const [rpc] = useRPC()
    const userListSelector = useSelector(state => state.control.userList || [])
    const role = useSelector(state => state.ui.role)
    const [userList, setUserList] = useState<UserModel[]>(userListSelector)
    
    useEffect(() => {
        rpc.getUserList().then(r => setUserList(r))
    }, [rpc])
    
    const filteredUserList = useMemo(() => userList.filter(user => hasAuthority(role, user.role)), [role, userList])
    
    const processedUserList = useMemo(() => {
        return filteredUserList.map(user => {
            
            return {
                ...user,
                previous_login_display: user.previous_login_info ? formatLoginTime(user.previous_login_info.timestamp) : "-",
                latest_login_display: user.latest_login_info ? formatLoginTime(user.latest_login_info.timestamp) : "-",
                previous_login_ip: user.previous_login_info ? user.previous_login_info.ip : "-",
                latest_login_ip: user.latest_login_info ? user.latest_login_info.ip : "-"
            }
        })
    }, [filteredUserList])
    
    const currentUsername = useSelector(state => state.user.username)
    const currentUser = useMemo(() => filteredUserList.find(u => u.username === currentUsername), [currentUsername, filteredUserList])
    const [selectedUser, setSelectedUser] = useState<UserModel>(currentUser ? currentUser : null)
    
    const displayNameExtractor = useCallback((item: UserModel, column: ColumnModel) => {
        if (column.key === "name") {
            return item
        }
        return item[column.key] || "-"
    }, [])
    
    const columns = useMemo<ColumnModel[]>(
        () => [
            {
                length: 1,
                name: t("username"),
                key: "username"
            },
            {
                length: 2,
                name: t("previous_login"),
                key: "previous_login_display"
            },
            {
                length: 2,
                name: t("previous_ip"),
                key: "previous_login_ip"
            },
            {
                length: 2,
                name: t("latest_login"),
                key: "latest_login_display"
            },
            {
                length: 2,
                name: t("latest_ip"),
                key: "latest_login_ip"
            }
        ],
        [t]
    )
    
    return (
        <PasswordContainer requiredRole="default" isFullscreen={true}>
            <SettingsPageContainer className="user-info" buttons={[]} backUrl="/settings/logs" searchable={true}>
                <SettingsList
                    columns={columns}
                    items={processedUserList}
                    listType="userInfo"
                    keyExtractor={keyExtractor}
                    classExtractor={classExtractor}
                    displayNameExtractor={displayNameExtractor}
                    selectedItem={selectedUser}
                    onItemSelected={setSelectedUser}
                />
            </SettingsPageContainer>
        </PasswordContainer>
    )
})
function formatLoginTime(timestamp: number): string {
    if (!timestamp) return "-"
    
    const dateObject = new Date(timestamp * 1000)
    
    const yearString = dateObject.getFullYear().toString().padStart(2, "0")
    const monthString = (dateObject.getMonth() + 1).toString().padStart(2, "0")
    const dayString = dateObject.getDate().toString().padStart(2, "0")
    const hourString = dateObject.getHours().toString().padStart(2, "0")
    const minuteString = dateObject.getMinutes().toString().padStart(2, "0")
    const secondString = dateObject.getSeconds().toString().padStart(2, "0")
    
    return `${dayString}/${monthString}/${yearString} ${hourString}:${minuteString}:${secondString}`
}